import React, { useRef, useEffect } from 'react';
import { nextAnimationFrame, wait } from '../../util/util';
import { guid } from 'redux-database/dist/util';
import panzoom, { PanZoom } from 'panzoom';
import { Point } from '../../model/seatmap/types';

interface Props {
  children: JSX.Element;
  virtualCoordinates: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
}

const VIEWPORT_HEIGHT = 500;
const TARGET_ZOOM = 3;

export class MapElement {
  private _element!: HTMLDivElement;
  offset: Point = [0, 0];
  zoom = 1;
  width = 1;
  virtualCoordinates!: Props['virtualCoordinates'];
  height = VIEWPORT_HEIGHT;
  id = guid();
  handle!: PanZoom;

  get element(): HTMLDivElement {
    return this._element;
  }

  set element(element: HTMLDivElement) {
    this._element = element;
    this.handle?.dispose();
    this.handle = panzoom(element);
  }

  zoomTo(rawPoint: Point, zoom = TARGET_ZOOM, animated = true): void {
    const pointFactor = this.width / this.virtualCoordinates.width;
    const point = [
      (rawPoint[0] - this.virtualCoordinates.x) * pointFactor,
      (rawPoint[1] - this.virtualCoordinates.y) * pointFactor,
    ];
    this.withAnimation(animated, () => {
      this.handle.zoomAbs(0, 0, 1);
      this.handle.moveTo(this.width * 0.5 - point[0], this.height * 0.5 - point[1]);
      this.handle.zoomAbs(this.width / 2, this.height / 2, zoom);
    });
  }

  reset(animated = true): void {
    this.withAnimation(animated, () => {
      this.handle.zoomAbs(0, 0, 1);
      this.handle.moveTo(0, 0);
    });
  }

  zoomBy(change: number, animated = true): void {
    this.withAnimation(animated, () => {
      this.handle.zoomTo(this.width / 2, this.height / 2, change);
    });
  }

  private async withAnimation(animated = true, func: () => void): Promise<void> {
    if (animated) {
      this.element.style.transition = 'transform 0.5s';
      await nextAnimationFrame();
      func();
      await wait(0.5);
      this.element.style.transition = '';
    } else {
      func();
    }
  }
}

export const Map = React.forwardRef(({ children, virtualCoordinates }: Props, forwardRef) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef(new MapElement());
  const ref = forwardRef as React.MutableRefObject<MapElement>;
  if (!ref.current) {
    ref.current = mapRef.current;
  }

  if (mapRef.current) mapRef.current.virtualCoordinates = virtualCoordinates;

  useEffect(() => {
    if (containerRef.current) {
      mapRef.current.element = containerRef.current;
      mapRef.current.width = containerRef.current.clientWidth;
      mapRef.current.height = containerRef.current.clientHeight;
    }
  }, []);

  return (
    <div className="seatmap__content" style={{ overflow: 'hidden' }}>
      <div ref={containerRef}>{children}</div>
    </div>
  );
});
